import api from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export interface OAuthConfig {
  google: { enabled: boolean };
}

export interface LinkedProviders {
  linked: {
    google: boolean;
  };
  available: OAuthConfig;
}

/**
 * OAuth Service - Frontend OAuth operations
 */
export const oauthService = {
  /**
   * Get OAuth configuration (which providers are enabled)
   */
  async getConfig(): Promise<OAuthConfig> {
    const response = await api.get('/oauth/config');
    return response.data;
  },

  /**
   * Redirect to Google OAuth for login
   */
  loginWithGoogle(options?: { organizationName?: string; invitationToken?: string; returnTo?: string }): void {
    const params = new URLSearchParams();
    if (options?.organizationName) params.set('organizationName', options.organizationName);
    if (options?.invitationToken) params.set('invitationToken', options.invitationToken);
    // Preserve returnTo so the user lands back where they were after OAuth login
    if (options?.returnTo) {
      window.sessionStorage.setItem('oauth_return_to', options.returnTo);
    }
    const query = params.toString();
    window.location.href = `${API_BASE_URL}/oauth/google${query ? `?${query}` : ''}`;
  },

  /**
   * Redirect to Google OAuth for linking
   * Note: The token is passed via the API interceptor, the redirect happens server-side
   */
  linkGoogle(): void {
    window.location.href = `${API_BASE_URL}/oauth/link/google`;
  },

  /**
   * Unlink an OAuth provider from the account
   */
  async unlinkProvider(provider: 'google'): Promise<void> {
    await api.delete(`/oauth/unlink/${provider}`);
  },

  /**
   * Get linked OAuth providers for current user
   */
  async getLinkedProviders(): Promise<LinkedProviders> {
    const response = await api.get('/oauth/providers');
    return response.data;
  },

  /**
   * Exchange an auth code for tokens (used after OAuth callback)
   */
  async exchangeCode(code: string, deviceFingerprint?: string): Promise<{
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    user: any;
  }> {
    const response = await api.post('/auth/exchange-code', {
      code,
      deviceFingerprint,
    });
    return response.data;
  },
};

export default oauthService;
